import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Grid,
  GridItem,
  Box,
  Text,
  useColorModeValue,
  Image,
  FormErrorMessage,
  FormControl,
  Button,
  Input,
} from "@chakra-ui/react";
import goldenAcorn from "assets/img/golden-acorns.svg";
import Burger from "assets/img/Burger.svg";
import CheeseBurger from "assets/img/Cheese-Burger.svg";
import simpleAcorn from "assets/img/simple_acorn.svg";
import giftCard from "assets/img/Gift_Card.png";
import { PrimayButton } from "components/customs/buttons";
import { schema } from "./schema";
import { FormProvider, useForm } from "react-hook-form";
import Card from "components/card/Card";
import { CustomFormInput } from "components/customs/Input";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { formatRewardType } from "lib/helpers";
import { CustomSelect } from "components/customs/select";
import { CustomFormDatePicker } from "components/customs/datePicker";
import { useEffect, useState, useRef } from "react";
import { API_SUCCESS_RESPONSE_TAG } from "lib/constants";
import { createRewardsThunk } from "store/slices/rewards";
import { formatPayloadDate } from "lib/helpers";
import { useRewardsSelector } from "store/slices/rewards";
import { useSearchParams } from "./reward";
import { updateRewardsThunk } from "store/slices/rewards";
import { checkRewardType } from "lib/helpers";
import { REWARD_OPTIONS } from "lib/helpers";
import { RANK_TYPES_OPTIONS } from "lib/helpers";
import { isBefore } from "date-fns";
import { REWARD_TYPES_REVERSE_MAP } from "lib/constants";
import { BACKEND_BASE_URL } from "lib/constants";
import { toast } from "react-toastify";


// ⭐ NEW DROPDOWN OPTIONS ADDED HERE
const PERFORMANCE_OPTIONS = [
  { label: "Top Hits", value: "top_hits" },
  { label: "Top Score", value: "top_score" },
  { label: "Most Games Played", value: "most_games_played" },
];

const ALLOWED_REWARD_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
];


export function RewardModal({
  isOpen,
  onClose,
  selectedDate,
  handleSelectDate,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const { searchParams } = useSearchParams();
  const id = searchParams.get("id");
  const rewardsState = useRewardsSelector();
  const rewards = rewardsState?.rewards;
  const reward = rewards?.data?.results?.find((r) => r.id === id);

  const defaultValues = {
    rewardValue: reward?.rewardValue ?? 1,
    rewardType: reward?.rewardType ?? REWARD_OPTIONS[0].value,
    cardCode: reward?.cardCode ?? "",
    cardType: reward?.cardType ?? "",
    cardImage: reward?.cardImage ?? null,
    rank: reward?.rank ?? 1,
    leaderboardType: reward?.leaderboardType ?? PERFORMANCE_OPTIONS[0].value,

    rewardDate:
      reward?.rewardDate?.split("T")?.[0] ??
      formatPayloadDate(
        isBefore(selectedDate, new Date()) ? new Date() : selectedDate
      ),
  };

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const {
    watch,
    handleSubmit,
    reset,
    register,
    clearErrors,
    formState: { errors, isSubmitting, dirtyFields },
  } = methods;

  useEffect(() => {
    register("cardImage");
  }, [register]);

  // Reset form when reward changes (for edit mode)
  useEffect(() => {
    if (reward) {
      reset({
        rewardValue: reward?.rewardValue ?? 1,
        rewardType: reward?.rewardType ?? REWARD_OPTIONS[0].value,
        cardCode: reward?.cardCode ?? "",
        cardType: reward?.cardType ?? "",
        cardImage: reward?.cardImage ?? null,
        rank: reward?.rank ?? 1,
        leaderboardType: reward?.leaderboardType ?? PERFORMANCE_OPTIONS[0].value,
        rewardDate:
          reward?.rewardDate?.split("T")?.[0] ??
          formatPayloadDate(
            isBefore(selectedDate, new Date()) ? new Date() : selectedDate
          ),
      });
    }
  }, [reward, reset, selectedDate]);

  useEffect(() => {
    return () => {
      history.push("/admin/rewards");
    };
  }, []);

  const newRewardDate = watch("rewardDate");
  useEffect(() => {
    if (!id) {
      handleSelectDate(newRewardDate);
    }
  }, [newRewardDate, id, handleSelectDate]);
  const onSubmit = async (data) => {
    let payload = data;
  
    if (!is_gift_card) {
      const { cardImage, cardCode, cardType, ...restPayload } = payload;
      payload = restPayload;
    }
  
    if (id) {
      payload = Object.keys(payload).reduce((acc, current) => {
        if (dirtyFields[current] || current === "leaderboardType") {
          acc[current] = payload[current];
        }
        return acc;
      }, {});
  
      payload.id = id;
  
      const res = await dispatch(
        updateRewardsThunk({ ...payload, selectedRewardDate: selectedDate })
      );
      if (res.payload === API_SUCCESS_RESPONSE_TAG) {
        onClose();
      }
      return;
    }
    // Create reward API call
    const res = await dispatch(createRewardsThunk({ ...payload }));
    if (res.payload === API_SUCCESS_RESPONSE_TAG) {
      onClose();
    }
  };
  
  const rewardType = watch("rewardType");
  const { is_gift_card, is_golden_acorns, is_simple_acorns } =
    checkRewardType(rewardType);
  console.log({ is_simple_acorns, is_golden_acorns, is_gift_card }, "is_gift_card");

  const cardImageValue = watch("cardImage");
  const [cardPreviewUrl, setCardPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const { setValue } = methods;
  const rewardCardImage =
    reward?.cardImage && !reward?.cardImage?.startsWith("http")
      ? `${BACKEND_BASE_URL}/${reward?.cardImage}`
      : reward?.cardImage;

  const handleCardImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!ALLOWED_REWARD_IMAGE_TYPES.includes(file.type)) {
      toast.error("Invalid file type. Please upload a PNG, JPG, GIF, or WEBP image.");
      setValue("cardImage", null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setValue("cardImage", event.target.files, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    clearErrors("cardImage");
  };

  useEffect(() => {
    if (!is_gift_card) {
      setCardPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }
    const file = cardImageValue?.[0] ?? cardImageValue;
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      setCardPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [cardImageValue, is_gift_card]);

  let previewImage = giftCard;
  let previewImageWidth = "350px";
  let previewImageHeigth = "230px";

  if (is_gift_card) {
    previewImage = cardPreviewUrl ?? rewardCardImage ?? giftCard;
    previewImageWidth = "330px";
    previewImageHeigth = "230px";
  }

  if (is_golden_acorns) {
    previewImage = CheeseBurger;
    previewImageWidth = "300px";
    previewImageHeigth = "380px";
  }

  if (is_simple_acorns) {
    previewImage = Burger;
    previewImageWidth = "300px";
    previewImageHeigth = "380px";
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <ModalHeader>
                <Text
                  color={textColor}
                  fontSize="40px"
                  fontWeight="700"
                  lineHeight="100%"
                >
                  {formatRewardType(
                    REWARD_TYPES_REVERSE_MAP[watch("rewardType")]
                  )}
                </Text>
              </ModalHeader>

              <ModalCloseButton
                borderRadius="50%"
                size="lg"
                bg="secondaryGray.900"
                color="white"
                fontWeight="bold"
                _hover={{ bg: "secondaryGray.900", color: "white" }}
              />

              <ModalBody>
                <Box pt="5">
                  <Card mb={{ base: "0px", lg: "20px" }} align="center">
                    <Grid
                      templateColumns={{
                        base: "repeat(1, 1fr)",
                        lg: "repeat(2, 1fr)",
                      }}
                      gap={{ base: 3, lg: 10 }}
                    >
                      <GridItem order={2}>
                        <Box
                          p="5"
                          bg="secondaryGray.400"
                          height="auto"
                          borderRadius="lg"
                          overflow="hidden"
                        >
                          <Box
                            p="5"
                            bg="secondaryGray.50"
                            borderRadius="lg"
                            overflow="hidden"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            minH="350px"
                          >
                            <Box>
                              <Box
                                width={previewImageWidth}
                                height={previewImageHeigth}
                                borderRadius="lg"
                                overflow="hidden"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Image
                                  objectFit="cover"
                                  width="292px"
                                  height="242px"
                                  src={previewImage}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                        {is_gift_card && (
                          <Box mt="4" textAlign="center">
                            <Input
                              type="file"
                              accept="image/*"
                              ref={fileInputRef}
                              display="none"
                              onChange={handleCardImageChange}
                            />
                            <Button
                              onClick={() => fileInputRef.current?.click()}
                              bg="linear-gradient(180deg, #9BA5D2 0%, #3A50AC 100%)"
                              color="white"
                              borderRadius="16px"
                              px="8"
                              py="6"
                              fontWeight="600"
                              _hover={{
                                bg: "linear-gradient(180deg, #8A94C1 0%, #2A409C 100%)",
                              }}
                            >
                              Upload Cover Image
                            </Button>
                            <FormControl isInvalid={errors?.cardImage} mt="2">
                              <FormErrorMessage justifyContent="center">
                                {errors?.cardImage?.message}
                              </FormErrorMessage>
                            </FormControl>
                            {cardImageValue?.[0]?.name && (
                              <Text fontSize="sm" color="gray.600" mt="2">
                                {cardImageValue[0].name}
                              </Text>
                            )}
                          </Box>
                        )}
                      </GridItem>

                      <GridItem order={1}>
                        <CustomSelect
                          styles={{
                            selectFont: 40,
                            selectWeight: "bold",
                            selectPadding: 5,
                          }}
                          options={REWARD_OPTIONS}
                          label="Reward Type"
                          name="rewardType"
                        />

                        {is_gift_card && (
                          <CustomFormInput
                            {...{
                              placeholder: "Code",
                              type: "text",
                              name: "cardCode",
                              label: "Gift Card# (code)",
                              isRequired: true,
                            }}
                          />
                        )}
                        {is_gift_card && (
                          <CustomFormInput
                            {...{
                              placeholder: "Card type (e.g., Amazon, Steam)",
                              type: "text",
                              name: "cardType",
                              label: "Gift Card Type",
                              isRequired: true,
                            }}
                          />
                        )}

                        <CustomFormInput
                          {...{
                            placeholder: is_gift_card ? "Amount" : "Quantity",
                            type: "text",
                            name: "rewardValue",
                            label: is_gift_card
                              ? "Gift Card (quantity)"
                              : is_golden_acorns
                              ? "Quantity(Cheese Burger)"
                              : "Quantity(Burger)",
                            isRequired: true,
                          }}
                        />

                        <CustomSelect
                          options={RANK_TYPES_OPTIONS}
                          label="Rank"
                          name="rank"
                        />

                        {/* ⭐ NEW DROPDOWN ADDED HERE */}
                        <CustomSelect
                          options={PERFORMANCE_OPTIONS}
                          label="Performance Type"
                          name="leaderboardType"
                        />

                        <CustomFormDatePicker
                          label="Reward Date"
                          name="rewardDate"
                        />

                        <PrimayButton
                          key="submit"
                          label={id ? "Update" : "Create"}
                          isLoading={isSubmitting}
                        />
                      </GridItem>
                    </Grid>
                  </Card>
                </Box>
              </ModalBody>
            </form>
          </FormProvider>
        </ModalContent>
      </Modal>
    </>
  );
}
